import { useEffect } from 'react';
import {
  CONSUMER_CMS_SHOPPING_CART,
  useCmsConfig,
  IPortabilityMobileCMS,
  productSpecificError,
  useGetPortabilityInfoMobile,
  IGenericErrorCMS,
  ErrorCodes,
  errorMock,
  IDigitalOperatorsConfig,
  ICommonLabelsInStepper,
  organizeCommonLabelsInStepper,
} from '@vfit/consumer/data-access';
import {
  API,
  regex,
  defineYupSchema,
  trackView,
  getClickToCallWithKeyOrUrl,
} from '@vfit/shared/data-access';
import { ShadowsGroup } from '@vfit/shared/components';
import { ICMSApiError, ICMSError } from '@vfit/shared/models';
import {
  organizePortabilityError,
  organizePortabilityNumberCard,
} from './portabilityNumberCardWinback.utils';
import PortabilityNumber from './components/PortabilityNumber/portabilityNumber';
import { ICheckoutData, IEditorialErrorConfig } from '../../checkout.models';
import { ID_FLOWS } from '../../checkout.constants';
import { checkErrorCustomer } from '../../checkout.utils';
import { Skeleton } from './portabilityNumberCardWinback.style';
import { DEFAULT_PORTABILITY } from '../../../iBuyMobile.context.data';
import { useCheckout } from '../../../iBuyMobile.context';
import { useAddressMobileFlow } from '../../hooks/UseAddressMobileFlow/useAddressMobileFlow';
import TaggingPortabilityNumberWinbackCard from './portabilityNumberCardWinback.tagging';
import { useQueryClient } from 'react-query';

const PortabilityNumberCardWinback = ({
  handleOnClose,
  handleOnChangeEnableGoNext,
  handleGoPrevSlide,
}: ICheckoutData) => {
  const queryClient = useQueryClient();
  const {
    currentStepKey,
    portability,
    isModalButtonSticky,
    product,
    customerFlow,
    checkoutErrors,
    setSlidesGoBack,
    setIsStartCustomerFlow,
    setShippingAddress,
    setBillingAddress,
    setPortability,
    setCurrentStepKey,
    setIsValidNumberPortability,
  } = useCheckout();
  const {
    data: dataAddress,
    isSuccess: isSuccessAddress,
    isLoading: isLoadingAddress,
    isError: isErrorAddress,
    errorMessage: errorMessageAddress,
  } = useAddressMobileFlow({
    onConfirmAddress: true,
  });
  const isLoadingCustomer =
    customerFlow?.postCustomer?.isLoading ||
    customerFlow?.patchCustomer?.isLoading ||
    customerFlow?.customerInfo?.isLoading ||
    customerFlow?.associateCustomer?.isLoading;
  const isSuccessCustomer =
    customerFlow?.postCustomer?.isSuccess ||
    (customerFlow?.patchCustomer?.isSuccess && customerFlow?.associateCustomer?.isSuccess);
  const isErrorCustomer =
    customerFlow?.postCustomer?.isError ||
    customerFlow?.patchCustomer?.isError ||
    customerFlow?.associateCustomer?.isError;
  const errorCustomer =
    customerFlow?.postCustomer?.error ||
    customerFlow?.patchCustomer?.error ||
    customerFlow?.associateCustomer?.error;
  const genericError = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_GENERIC_ERROR_MOBILE
  ) as IGenericErrorCMS;
  const forcePortabilityWant = product?.isRequiredPortability || product?.isWinbackLegacy;
  const { buttonGenericError, urlGenericErrorCallMeNow } = productSpecificError(product);
  const cmsApiKey = API.CMS_GET_WINBACK_DATA;
  const portabilityNumberCardCMS = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    cmsApiKey
  ) as IPortabilityMobileCMS;
  const { blacklist } =
    (useCmsConfig(
      CONSUMER_CMS_SHOPPING_CART,
      API.CMS_GET_DIGITAL_BUNDLE_OPERATORS
    ) as IDigitalOperatorsConfig) || {};
  const { title, description, label, validationMessage } =
    organizePortabilityNumberCard(portabilityNumberCardCMS);
  const { titleError, messageError } = organizePortabilityError(portabilityNumberCardCMS);

  const { errorcomponent: errorComponentOrder } =
    (useCmsConfig(
      CONSUMER_CMS_SHOPPING_CART,
      API.CMS_GET_RECURRENT_ORDER_ERROR_MOBILE
    ) as ICMSApiError) || {};
  const orderErrors = errorComponentOrder?.errors || [];

  const { errorcomponent: errorComponentCustomer } =
    (useCmsConfig(CONSUMER_CMS_SHOPPING_CART, API.CMS_GET_CUSTOMER_ERROR_MOBILE) as ICMSApiError) ||
    {};
  const commonLabels = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_STEPPER_COMMON_LABELS_MOBILE
  ) as ICommonLabelsInStepper;
  const { goBackInStepper } = organizeCommonLabelsInStepper(commonLabels);

  const customerErrors = errorComponentCustomer?.errors || [];

  const errors: ICMSError[] = [...orderErrors, ...customerErrors];

  const {
    isLoading: isLoadingPortInfo,
    isError: isErrorPortInfo,
    isSuccess: isSuccessPortInfo,
    error: errorPortIn,
  } = useGetPortabilityInfoMobile(
    portabilityNumberCardCMS.portabilitymobile.operatorList,
    portabilityNumberCardCMS.portabilitymobile.operatorsOrder,
    blacklist?.elements
  );

  const checkPortabilityValidation = () => {
    if (portability?.portabilityNumber) {
      const isMatches = portability.portabilityNumber.match(regex['mobilePhoneNumber']);
      handleOnChangeEnableGoNext?.(
        !!isMatches && isSuccessPortInfo && !isLoadingAddress && isSuccessCustomer
      );
    }
  };

  useEffect(() => {
    handleOnChangeEnableGoNext?.(isSuccessPortInfo && !isLoadingAddress && isSuccessCustomer);
  }, [isLoadingPortInfo, isLoadingAddress, isLoadingCustomer, isSuccessCustomer]);

  useEffect(() => {
    if (isSuccessAddress && dataAddress) {
      setBillingAddress(dataAddress);
      setShippingAddress(dataAddress);
      setIsStartCustomerFlow(true);
    }
  }, [isSuccessAddress, dataAddress]);

  useEffect(() => {
    if (isErrorAddress) {
      const closeModalAction = () => {
        handleGoPrevSlide?.();
      };
      const mockError = errorMock('address', {
        url: 'validateAddress',
        status: '500',
        statusText: '500',
      });
      checkoutErrors?.showAndTrackError?.(
        genericError,
        closeModalAction,
        mockError,
        undefined,
        errorMessageAddress,
        closeModalAction,
        goBackInStepper || 'Torna indietro',
        () =>
          getClickToCallWithKeyOrUrl(
            queryClient,
            product?.genericErrorCallMeNow?.url || '',
            product,
            closeModalAction,
            currentStepKey,
            mockError
          ),
        product?.removeCTC ? undefined : product?.genericErrorCallMeNow?.label,
        isModalButtonSticky,
        true
      );
    }
  }, [isErrorAddress]);

  useEffect(() => {
    if (isErrorPortInfo) {
      const action = () => {
        if (handleGoPrevSlide) handleGoPrevSlide();
      };
      checkoutErrors?.showAndTrackError?.(
        genericError,
        action,
        errorMock('portininfo', errorPortIn),
        titleError,
        messageError,
        action,
        goBackInStepper || 'Torna indietro',
        () =>
          getClickToCallWithKeyOrUrl(
            queryClient,
            urlGenericErrorCallMeNow,
            product,
            action,
            currentStepKey,
            errorMock('portininfo', errorPortIn)
          ),
        product?.removeCTC ? undefined : buttonGenericError,
        isModalButtonSticky,
        true
      );
    }
  }, [isErrorPortInfo]);

  useEffect(() => {
    if (isErrorCustomer) {
      const errorDetail: IEditorialErrorConfig = checkErrorCustomer(
        errors,
        errorCustomer?.errorCode || '',
        {
          title: genericError?.genericerror?.title || 'Ops',
          description: genericError?.genericerror?.description || '',
        }
      );
      trackView({
        event_name: 'page_error',
        page_error: `${errorCustomer?.errorType}| ${errorCustomer?.errorMessage} | ${errorDetail.message}`,
        event_category: 'error',
        page_error_code: errorCustomer?.errorType,
        page_type: 'error page',
      });
      const getAction = () => {
        customerFlow?.resetPatchAndPost?.(customerFlow);
        switch (errorCustomer?.errorCode) {
          case ErrorCodes.CREATE_CUSTOMER_FISCAL_CODE_ALREADY_PRESENT:
          case ErrorCodes.CREATE_CUSTOMER_DOCUMENT_ALREADY_PRESENT:
            setSlidesGoBack(1);
            break;
          case ErrorCodes.CREATE_CUSTOMER_MISSING_NATION_ADDRESS:
            setSlidesGoBack(2);
            break;
          case ErrorCodes.CREATE_CUSTOMER_PHONE_NUMBER_ALREADY_PRESENT:
          case ErrorCodes.CREATE_CUSTOMER_EMAIL_ADDRESS_ALREADY_PRESENT:
          case ErrorCodes.CREATE_CUSTOMER_CONTACT_ALREADY_PRESENT:
            setSlidesGoBack(3);
            break;
          case ErrorCodes.ASSOCIATE_CUSTOMER_BLOCKED:
            if (errorDetail?.isPopup) {
              getClickToCallWithKeyOrUrl(
                queryClient,
                errorDetail?.url || urlGenericErrorCallMeNow,
                product,
                () => {
                  if (handleOnClose) {
                    handleOnClose();
                  }
                },
                currentStepKey,
                errorCustomer
              );
            } else if (handleOnClose) {
              handleOnClose();
            }
            break;
          default:
            if (handleOnClose) {
              handleOnClose();
            }
            break;
        }
      };
      checkoutErrors?.showAndTrackError?.(
        {
          genericerror: {
            title: errorDetail?.title || '',
            description: errorDetail?.message || '',
            buttonLabel: errorDetail?.actionText || 'Ti chiamiamo noi',
          },
        },
        getAction,
        errorCustomer,
        '',
        '',
        () => getAction(),
        ''
      );
    }
  }, [isErrorCustomer]);

  useEffect(() => {
    checkPortabilityValidation();
  }, [portability]);

  useEffect(() => {
    setCurrentStepKey(ID_FLOWS.PORTABILITY_NUMBER);
    checkPortabilityValidation();
    if (forcePortabilityWant) {
      localStorage.setItem('portability', '1');
    }
  }, []);

  const onChangeData = (telephoneNumber: string) => {
    setIsValidNumberPortability(false);
    setPortability({
      ...DEFAULT_PORTABILITY,
      portabilityWant: forcePortabilityWant ? true : portability.portabilityWant,
      portabilityNumber: telephoneNumber || '',
    });
  };

  const getPortabilityCard = () => {
    if (isLoadingPortInfo || isLoadingAddress || isLoadingCustomer) {
      return (
        <Skeleton>
          <ShadowsGroup heights={70} quantity={3} />
        </Skeleton>
      );
    }
    return (
      <PortabilityNumber
        validationMessage={validationMessage}
        title={title}
        description={description}
        label={label}
        value={portability.portabilityNumber}
        onChangeData={onChangeData}
        disabled
        schema={defineYupSchema('phoneNumber')}
      />
    );
  };

  return (
    <>
      <TaggingPortabilityNumberWinbackCard />
      {getPortabilityCard()}
    </>
  );
};

export default PortabilityNumberCardWinback;

